import React, { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BlockIcon from "@mui/icons-material/Block";
import {
  BILLING_UNLOCK,
  COST_DRIVER,
  GCP_PROJECTS,
  INFRA,
  INFRA_NOT_USED,
} from "../infrastructure";

const DRIVER_LABEL = {
  [COST_DRIVER.HIGH]: "High cost driver",
  [COST_DRIVER.MEDIUM]: "Medium",
  [COST_DRIVER.LOW]: "Low",
  [COST_DRIVER.NONE]: "No direct cost",
};

const CATEGORIES = ["All", ...new Set(INFRA.map((i) => i.category))];

/** One environment's column inside a service's drill-down. */
function EnvColumn({ envKey, env, label }) {
  if (!env?.present && !env?.resources?.length) {
    return (
      <div className={`co-env-col co-env-col--${envKey}`}>
        <div className="co-env-head">
          <span className={`co-env-pill co-env-pill--${envKey}`}>{label}</span>
          <span className="co-env-scale">{env?.scale || "Not present"}</span>
        </div>
        <p className="co-empty">Not deployed in this environment.</p>
      </div>
    );
  }

  return (
    <div className={`co-env-col co-env-col--${envKey}`}>
      <div className="co-env-head">
        <span className={`co-env-pill co-env-pill--${envKey}`}>{label}</span>
        <span className="co-env-scale">{env.scale}</span>
      </div>
      <ul className="co-resource-list">
        {(env.resources || []).map((r) => (
          <li key={r.name} className={r.flag ? "is-flagged" : ""}>
            <div className="co-resource-name">
              {r.flag ? <WarningAmberIcon fontSize="inherit" /> : null}
              {r.name}
            </div>
            {r.detail ? <div className="co-resource-detail">{r.detail}</div> : null}
            {r.ref ? <div className="co-resource-ref">{r.ref}</div> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function InfraBreakdown() {
  const [category, setCategory] = useState("All");
  const [expanded, setExpanded] = useState(null);

  const rows = useMemo(
    () =>
      category === "All"
        ? INFRA
        : INFRA.filter((i) => i.category === category),
    [category],
  );

  return (
    <div className="co-infra">
      <div className="co-projects">
        {Object.entries(GCP_PROJECTS).map(([key, project]) => (
          <div className={`co-project co-project--${key}`} key={key}>
            <span className={`co-env-pill co-env-pill--${key}`}>
              {project.label}
            </span>
            <div className="co-project-id">{project.id}</div>
            <div className="co-project-num">Project #{project.number}</div>
            <div className="co-project-note">{project.note}</div>
          </div>
        ))}
      </div>

      <div className="co-disclaimer" style={{ marginBottom: 16 }}>
        <WarningAmberIcon fontSize="small" />
        <span>
          No dollar figures here: there is no GCP billing access. What this
          shows instead is the <strong>structure and scale</strong> of what runs
          — every service, every named resource, split by environment — so
          relative cost is at least reasonable to reason about. See "How to
          unlock real numbers" at the bottom.
        </span>
      </div>

      <div className="co-filters" style={{ marginBottom: 12 }}>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            className={`co-filter-chip ${category === c ? "is-active" : ""}`}
            onClick={() => setCategory(c)}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="co-infra-list">
        {rows.map((item) => {
          const isOpen = expanded === item.id;
          return (
            <div className="co-infra-item" key={item.id}>
              <button
                type="button"
                className="co-infra-head"
                onClick={() => setExpanded(isOpen ? null : item.id)}
              >
                {isOpen ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
                <div className="co-infra-title">
                  <span className="co-infra-name">{item.service}</span>
                  <span className="co-infra-what">{item.what}</span>
                </div>
                <div className="co-infra-meta">
                  <span className={`co-driver co-driver--${item.costDriver}`}>
                    {DRIVER_LABEL[item.costDriver]}
                  </span>
                  <span className="co-infra-envs">
                    {item.prod?.present ? (
                      <span className="co-env-pill co-env-pill--prod">prod</span>
                    ) : null}
                    {item.dev?.present ? (
                      <span className="co-env-pill co-env-pill--dev">dev</span>
                    ) : null}
                  </span>
                </div>
              </button>

              {isOpen ? (
                <div className="co-infra-detail">
                  <p className="co-cost-model">
                    <strong>How it bills:</strong> {item.costModel}
                  </p>
                  <div className="co-env-grid">
                    <EnvColumn envKey="prod" env={item.prod} label="Production" />
                    <EnvColumn envKey="dev" env={item.dev} label="Development" />
                  </div>
                  {item.notes ? (
                    <p className="co-note co-note--warn">{item.notes}</p>
                  ) : null}
                  <p className="co-resource-ref">Evidence: {item.evidence}</p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="co-section">
        <h2>Checked and confirmed NOT in use</h2>
        <p className="co-sub" style={{ marginBottom: 12 }}>
          Recorded so nobody re-audits them or budgets for them.
        </p>
        <ul className="co-list">
          {INFRA_NOT_USED.map((item) => (
            <li className="co-list-item" key={item.name}>
              <BlockIcon fontSize="small" style={{ color: "#94a3b8" }} />
              <div>
                <p className="co-list-title">{item.name}</p>
                <p className="co-list-detail">{item.note}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="co-section">
        <h2>How to unlock real numbers</h2>
        <p className="co-sub" style={{ marginBottom: 12 }}>
          Ordered by leverage. The first item alone would turn most of this page
          from structure into dollars.
        </p>
        <div className="ou-table-wrap">
          <table className="ou-table">
            <thead>
              <tr>
                <th>Step</th>
                <th>Priority</th>
                <th>What it unlocks</th>
                <th>Effort</th>
              </tr>
            </thead>
            <tbody>
              {BILLING_UNLOCK.map((item) => (
                <tr key={item.step}>
                  <td>{item.step}</td>
                  <td>
                    <span
                      className={`co-priority co-priority--${item.priority}`}
                    >
                      {item.priority}
                    </span>
                  </td>
                  <td>{item.impact}</td>
                  <td>{item.effort}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
